import { formatDate, formatQuantity } from '@/features/warehouse/format';
import type { Batch, Receipt, Transfer, WriteOff } from '@/features/warehouse/types';
import type { ProductSpreadItem } from '@/features/products-spread/types';

/** Значения строки журнала: суффикс поля (journals.field.*) → текст. */
export type RowValues = Record<string, string>;

/** Раскладка одного дня недели журнала. */
export interface SpreadDay {
  /** гггг-мм-дд */
  date: string;
  items: ProductSpreadItem[];
}

/** Количества складываются из дробей — без округления вылезает 0,30000000000000004. */
function round(value: number): number {
  return Math.round(value * 1000) / 1000 + 0;
}

function byName(a: { productName: string }, b: { productName: string }): number {
  return a.productName.localeCompare(b.productName, 'ru');
}

/** Ведомость выдачи (ЖУРН-05): строка — продукт раскладки дня, выдаётся всё по меню. */
export function issueRows(days: SpreadDay[]): RowValues[] {
  return days.flatMap((day) =>
    [...day.items]
      .filter((item) => item.total > 0)
      .sort(byName)
      .map((item) => ({
        date: formatDate(day.date),
        product: item.productName,
        unit: item.unit,
        menuQuantity: formatQuantity(round(item.total)),
        toIssue: formatQuantity(round(item.total)),
      }))
  );
}

/** Остаток по продуктам: сумма остатков открытых партий. */
function balanceByProduct(batches: Batch[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const batch of batches) {
    out.set(batch.productId, (out.get(batch.productId) ?? 0) + batch.remaining);
  }
  return out;
}

/**
 * Заявка поставщику (ЖУРН-07): потребность недели по раскладке против
 * текущего остатка склада; заказываем только недостающее.
 */
export function orderRows(days: SpreadDay[], batches: Batch[], period: string): RowValues[] {
  const need = new Map<string, { productName: string; unit: string; total: number }>();
  for (const item of days.flatMap((day) => day.items)) {
    const current = need.get(item.productId);
    if (current) current.total += item.total;
    else
      need.set(item.productId, {
        productName: item.productName,
        unit: item.unit,
        total: item.total,
      });
  }
  const balance = balanceByProduct(batches);

  return [...need.entries()]
    .map(([productId, item]) => ({ ...item, stock: balance.get(productId) ?? 0 }))
    .filter((item) => item.total > 0)
    .sort(byName)
    .map((item) => ({
      period,
      product: item.productName,
      unit: item.unit,
      need: formatQuantity(round(item.total)),
      stockBalance: formatQuantity(round(item.stock)),
      toOrder: formatQuantity(round(Math.max(item.total - item.stock, 0))),
    }));
}

/** Движение склада объекта, из которого считается товарный отчёт. */
export interface StockMovements {
  /** Открытые партии сейчас — остаток на сегодня. */
  batches: Batch[];
  /** Накладные, списания и перемещения с начала периода и до сегодня. */
  receipts: Receipt[];
  writeOffs: WriteOff[];
  transfers: Transfer[];
}

interface ReportLine {
  productName: string;
  unit: string;
  /** Остаток сейчас. */
  now: number;
  income: number;
  expense: number;
  /** Приход и расход после конца периода — их откатываем, чтобы получить остаток на конец. */
  laterIncome: number;
  laterExpense: number;
}

/**
 * Товарный отчёт (ЖУРН-06) за [from..to]. Бэкенд отдаёт только текущий
 * остаток, поэтому остаток на конец периода получаем, откатывая движения
 * после него, а остаток на начало — откатывая ещё и движения периода.
 */
export function stockReportRows(
  objectId: string,
  movements: StockMovements,
  from: string,
  to: string,
  period: string
): RowValues[] {
  const lines = new Map<string, ReportLine>();
  const lineOf = (productId: string, productName: string, unit: string): ReportLine => {
    let line = lines.get(productId);
    if (!line) {
      line = { productName, unit, now: 0, income: 0, expense: 0, laterIncome: 0, laterExpense: 0 };
      lines.set(productId, line);
    }
    return line;
  };
  const move = (
    date: string,
    item: { productId: string; productName: string; unit: string; quantity: number },
    direction: 'in' | 'out'
  ) => {
    if (date < from) return;
    const line = lineOf(item.productId, item.productName, item.unit);
    const inPeriod = date <= to;
    if (direction === 'in') {
      if (inPeriod) line.income += item.quantity;
      else line.laterIncome += item.quantity;
    } else if (inPeriod) {
      line.expense += item.quantity;
    } else {
      line.laterExpense += item.quantity;
    }
  };

  for (const batch of movements.batches) {
    lineOf(batch.productId, batch.productName, batch.unit).now += batch.remaining;
  }
  for (const receipt of movements.receipts) {
    for (const item of receipt.items) move(receipt.date, item, 'in');
  }
  for (const writeOff of movements.writeOffs) {
    for (const item of writeOff.items) move(writeOff.date, item, 'out');
  }
  for (const transfer of movements.transfers) {
    const direction = transfer.toObjectId === objectId ? 'in' : 'out';
    for (const item of transfer.items) move(transfer.date, item, direction);
  }

  return [...lines.values()]
    .map((line) => {
      const closing = round(line.now - line.laterIncome + line.laterExpense);
      const opening = round(closing - line.income + line.expense);
      return { ...line, opening, closing };
    })
    .filter(
      (line) => line.opening !== 0 || line.income !== 0 || line.expense !== 0 || line.closing !== 0
    )
    .sort(byName)
    .map((line) => ({
      period,
      product: line.productName,
      opening: formatQuantity(line.opening, line.unit),
      income: formatQuantity(round(line.income), line.unit),
      expense: formatQuantity(round(line.expense), line.unit),
      closing: formatQuantity(line.closing, line.unit),
    }));
}
